import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  fetchCollectionStructure,
  normalizeCollectionSections,
} from '@/features/collections/hooks/useCollectionsQuery';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { collectionKeys } from '@/shared/query/queryKeys';

/**
 * Route-driven collection selection.
 *
 * The store keeps only which collection is open; the collection's structure is
 * read through query hooks (useDisplayedCollectionStructure).
 */
export function useCollectionSelection() {
  const queryClient = useQueryClient();
  const setCurrentCollection = useCollectionStore(state => state.setCurrentCollection);
  const resetCurrentCollection = useCollectionStore(state => state.resetCurrentCollection);

  const setCollection = useCallback(
    async (collectionId: string | null) => {
      if (!collectionId) {
        resetCurrentCollection();
        return null;
      }

      const collection = await queryClient.fetchQuery({
        queryKey: collectionKeys.detail(collectionId),
        queryFn: () => normalizeCollectionSections(collectionId),
      });

      if (!collection) {
        resetCurrentCollection();
        return null;
      }

      setCurrentCollection(collection);
      const structure = await queryClient.fetchQuery({
        queryKey: collectionKeys.structure(collection.id),
        queryFn: () => fetchCollectionStructure(collection.id),
      });
      return { ...collection, structure };
    },
    [queryClient, resetCurrentCollection, setCurrentCollection]
  );

  return {
    setCollection,
  };
}
